/**
 * 案例层数据 —— 每个通道一份。
 *
 * 三层结构，从"能做"到"做过"逐层收窄：
 *   forms   能交付什么形态（App / SaaS / 小程序 / API / 私有化…）
 *   domains 能进哪些业务领域——每条写死「客户的问题 → 交付什么 → 拿什么验收」，
 *           验收标准这一栏是重点：说得出验收标准，才像真做过；只说能做，谁都会说。
 *   works   真做过的东西
 *
 * ── 内容口径（别在后续改动里松掉）──────────────────────────────
 * 1. works 里只写仓库里查得到依据的项目，状态照实标，不编客户名、不编收益数字。
 * 2. **url 只挂实测能打开的地址**。2026-09-01 实测：apps.ts 里 20 个线上地址只有
 *    5 个返回 200，其余是 526（Cloudflare 回源证书无效）或连不上。挂死链比不挂
 *    更伤——上次首页改版就是被"半成品链接"拖垮的。加新链接前先 curl 一遍。
 * 3. 量化方向只承接工程交付，边界声明见 quant.boundary，不要删。
 */

export type Status = 'live' | 'beta' | 'building' | 'review';

export const STATUS_LABEL: Record<Status, string> = {
  live: '线上可访问',
  beta: '内测中',
  building: '在建',
  review: '提审中',
};

export type Form = {
  label: string;
  en: string;
  deliver: string;
};

export type Domain = {
  name: string;
  /** 客户真正头疼的事，不是技术难点 */
  pain: string;
  /** 交付物是什么 */
  deliver: string;
  /** 拿什么判定做成了——这一栏最值钱 */
  accept: string;
};

export type Work = {
  name: string;
  what: string;
  platform: string;
  status: Status;
  /** 只在实测可访问时填 */
  url?: string;
  tech: string[];
};

export type CaseSet = {
  id: string;
  forms: Form[];
  domains: Domain[];
  works: Work[];
  boundary: string;
};

export const caseSets: Record<string, CaseSet> = {
  /* ─────────────────────────── CH.01 视觉动捕 ─────────────────────────── */
  motion: {
    id: 'motion',
    forms: [
      { label: 'iOS App', en: 'IOS', deliver: '端侧推理，视频不出设备；含上架材料与隐私合规文案' },
      { label: '安卓 App', en: 'ANDROID', deliver: '同一套解算，按机型算力分档降级，低端机也能跑' },
      { label: '微信小程序', en: 'MINI PROGRAM', deliver: '扫码即用，适合门店、医院这类装不动 App 的场景' },
      { label: 'Web 应用', en: 'WEB', deliver: '浏览器内推理，用来做试点和给决策层演示' },
      { label: '开放 API', en: 'API', deliver: '只回传骨骼点与判定结果，视频不进接口' },
      { label: '私有化部署', en: 'ON-PREM', deliver: '端侧盒子或内网服务，模型与数据都不出场区' },
      { label: '源码交付', en: 'SOURCE', deliver: '含二次开发文档与规则表结构，团队可自己接着做' },
    ],
    domains: [
      {
        name: '体育训练',
        pain: '一对一教练贵又排不上，学员自己看回放看不出到底差在哪。',
        deliver: 'iOS / 安卓 App，每个运动项目配一份判定规则表，新增项目是加规则不是重做。',
        accept: '能指到具体那一帧——「第 1.2 秒右肘比示范早了 18°」，而不是给一个 85 分。',
      },
      {
        name: '康复理疗',
        pain: '出院后居家康复没人纠正，动作走样还在练；依从性也统计不到，复诊全靠患者自述。',
        deliver: '小程序（患者扫码练）+ 医师后台（看周报与异常动作片段）。',
        accept: '关节活动度逐周曲线 + 训练完成率，格式能直接进病历。',
      },
      {
        name: '健身房与私教 SaaS',
        pain: '器械动作不规范练出伤，门店担责；好私教的经验又复制不到分店。',
        deliver: '门店端侧盒子 + 管理后台 + API，接现有会员与排课系统。',
        accept: '动作规范率与计数准确率，按门店、按教练横向对比。',
      },
      {
        name: '工业动作规范与安全',
        pain: '标准作业动作、护具佩戴靠人盯，抽查覆盖率低，出事后才回看录像。',
        deliver: '端侧盒子 + 告警回调，画面不出厂区，只回传事件。',
        accept: '漏报率与误报率，用双盲抽帧复核，不看自评。',
      },
      {
        name: '舞蹈与才艺教学',
        pain: '跟练视频是单向的，学员不知道自己哪一拍崩了，练完也不知道有没有进步。',
        deliver: 'App，与示范动作做时序对齐后逐帧比对打分。',
        accept: '对齐后的偏差热力图——哪一拍、哪个关节，看得见。',
      },
      {
        name: '适老照护',
        pain: '独居老人跌倒发现晚；装摄像头家属又不放心画面传上云。',
        deliver: '端侧设备 + 告警推送，推理全在设备上，画面不出设备。',
        accept: '跌倒召回率，以及长时间静置场景下的误报次数。',
      },
    ],
    works: [
      {
        name: '11 项运动动捕应用合集',
        what: '一套姿态解算覆盖 HIIT、瑜伽、普拉提、力量、拉伸，以及网球、羽毛球、篮球、足球、高尔夫、游泳。代码按二次开发的结构组织，加一个新项目是加一份判定规则。',
        platform: 'iOS · 安卓',
        status: 'building',
        tech: ['端侧推理', '多项目规则表', '含源码'],
      },
      {
        name: '智能羽毛球教练',
        what: '单项做深的样本：把一次挥拍切成引拍、击球、随挥三段分别判定，而不是对整段动作打一个总分。',
        platform: 'Web',
        status: 'beta',
        tech: ['相位切分', '逐段判定'],
      },
      {
        name: 'AR 私人健身教练',
        what: '浏览器内实时姿态跟随，用来验证「不装 App 也能跑」这条路径到底可行到什么程度。',
        platform: 'Web',
        status: 'building',
        tech: ['浏览器内推理', 'AR 叠加'],
      },
      {
        name: 'AR 手语学习平台',
        what: '换的是关键点集合——手部而非全身骨架。用它量同一套管线迁移到新关键点集的成本。',
        platform: 'Web',
        status: 'building',
        tech: ['手部关键点', '管线复用'],
      },
      {
        name: '红包元宝圣诞树 AR 卡片',
        what: '轻量 AR 的投放形态：微信里打开即用，用来验证节庆场景下的到达率。',
        platform: '移动网页',
        status: 'live',
        url: 'https://minddock.ai/tree-card/',
        tech: ['移动端 AR', '免安装'],
      },
      {
        name: 'Neon 吉他英雄',
        what: '实时手势识别驱动的交互原型，验证低延迟手势输入在浏览器里的可用边界。',
        platform: 'Web',
        status: 'live',
        url: 'https://neon-guitar-hero.minddock.ai',
        tech: ['手势识别', '低延迟'],
      },
    ],
    boundary:
      '状态按项目实际进度标注：未标「线上可访问」的表示仍在建或内测，不作已交付承诺。挂出的链接都经过实测。',
  },

  /* ─────────────────────────── CH.02 智能体 ─────────────────────────── */
  agent: {
    id: 'agent',
    forms: [
      { label: 'Web 后台 / SaaS', en: 'SAAS', deliver: '多租户或单租户皆可，带运行留痕与回放界面' },
      { label: '微信小程序', en: 'MINI PROGRAM', deliver: '面向一线员工与终端用户的轻入口' },
      { label: '安卓 / iOS App', en: 'MOBILE', deliver: '把智能体装进产品里，而不是做成一个独立聊天框' },
      { label: '桌面端', en: 'DESKTOP', deliver: '需要读本地文件、连内网工具的场景' },
      { label: '浏览器扩展', en: 'EXTENSION', deliver: '嵌进已有工作流，不要求用户换系统' },
      { label: '开放 API', en: 'API', deliver: '按任务而非按对话计费的接口形态' },
      { label: '私有化 / 断网部署', en: 'ON-PREM', deliver: '本地模型 + 编排，全链路不出内网' },
      { label: '源码交付', en: 'SOURCE', deliver: '编排配置与工具定义分离，团队可自行扩工具' },
    ],
    domains: [
      {
        name: '客服与售后',
        pain: '知识散在文档、工单和群聊里，同一个问题两个客服答得不一样；上了机器人又怕它乱答。',
        deliver: '问答服务 + 转人工链路 + 全程留痕，可私有化；答不准就转人工，不硬答。',
        accept: '自助解决率、转人工率，以及抽检的错答召回——三个一起看才有意义。',
      },
      {
        name: '内容与营销运营',
        pain: '平台多、形态多，产量靠堆人；但也没人敢让它全自动发出去。',
        deliver: '编排后台 + 人工审核卡点，过审才出稿；素材与话术版本可回溯。',
        accept: '单条成本、一次过审率、违规拦截数。',
      },
      {
        name: '研发与运维提效',
        pain: '重复的排查、发布、巡检占掉大半时间，但又不敢让脚本自己动生产环境。',
        deliver: '内网私有化编排 + 工具白名单，危险操作强制人工确认。',
        accept: '人工介入次数与平均处理时长，对比接入前后。',
      },
      {
        name: '单据与合规抽取',
        pain: '合同、票据、检测报告靠人读，量一大就漏；漏了往往到出问题才发现。',
        deliver: '抽取 API + 置信度分流，低置信度自动进人审队列。',
        accept: '逐字段准确率，以及进人审的比例——后者决定它到底省不省人。',
      },
      {
        name: '产品内嵌智能体',
        pain: '长任务跑到一半挂了，用户只看到一个转圈，重来一遍还可能再挂。',
        deliver: '可重入编排 + 逐步留痕，失败只重跑失败那一层，中断能续。',
        accept: '长任务完成率、断点续跑成功率。',
      },
      {
        name: '断网与私有化部署',
        pain: '数据不能出内网，但业务又确实想用上模型能力。',
        deliver: '本地模型 + 编排 + 按难度的成本路由，全链路不出网。',
        accept: '内网压测吞吐，以及一份「无外网连接」的审计记录。',
      },
    ],
    works: [
      {
        name: 'Anchor · 每晚三分钟的复盘工具',
        what: '一次刻意的克制实践：产品上限定它不给建议、不做评价，只把你说过的话记下来连起来。约束住智能体的话语权，比让它多说难得多。',
        platform: 'iOS',
        status: 'review',
        tech: ['长期记忆', '产品化约束'],
      },
      {
        name: 'Rusty',
        what: '面向陪伴场景的对话产品，用来打磨长对话下的人格一致性与上下文取舍。',
        platform: 'Web',
        status: 'live',
        url: 'https://rusty.minddock.ai/',
        tech: ['长对话', '人格一致性'],
      },
      {
        name: 'FreeTokenRouter',
        what: '按任务难度和成本在多家模型之间路由——「成本路由」那条能力的自用验证床。',
        platform: 'Web',
        status: 'beta',
        tech: ['成本路由', '多模型', 'token 预算'],
      },
      {
        name: 'EasyClaw',
        what: '把命令行形态的智能体装进桌面端，处理本地文件与内网工具的读写权限边界。',
        platform: '桌面端',
        status: 'beta',
        tech: ['本地工具', '权限边界'],
      },
      {
        name: 'EchoMe 语音秘书',
        what: '语音入口的智能体：难点不在识别，在于说了一半被打断之后怎么接住。',
        platform: '安卓',
        status: 'building',
        tech: ['语音交互', '打断恢复'],
      },
      {
        name: 'AI 工程知识库',
        what: '把编排、评测、成本这几摊工程实践整理成可检索的结构，也是对外讲做法的底稿。',
        platform: 'Web',
        status: 'live',
        url: 'https://minddock.ai/ai-engineering-hub',
        tech: ['知识组织', '工程实践'],
      },
    ],
    boundary:
      '状态按项目实际进度标注：未标「线上可访问」的表示仍在建、内测或提审中，不作已交付承诺。挂出的链接都经过实测。',
  },

  /* ─────────────────────────── CH.03 量化 ─────────────────────────── */
  quant: {
    id: 'quant',
    forms: [
      { label: 'Web 监控台', en: 'DASHBOARD', deliver: '净值、持仓、风控阈值与告警在一块屏上' },
      { label: '私有化部署', en: 'ON-PREM', deliver: '策略与密钥留在自己的机器上，不托管' },
      { label: '开放 API', en: 'API', deliver: '信号、下单、对账三段接口分开，可只用其中一段' },
      { label: '桌面端', en: 'DESKTOP', deliver: '本地跑研究与回测，不上传行情与持仓' },
      { label: '源码交付', en: 'SOURCE', deliver: '回测与实盘同源的框架本体，含撮合模拟层' },
    ],
    domains: [
      {
        name: '个人与小团队自动交易',
        pain: '手工盯盘扛不住 7×24，情绪更扛不住；规则想得清楚，执行时总走样。',
        deliver: '私有化执行服务 + 风控参数面板 + 异常告警，策略与密钥都不出自己的机器。',
        accept: '实盘与回测的信号一致率；以及断网、超时、拒单时是否按预期自动停机。',
      },
      {
        name: '策略研究与回测平台',
        pain: '「回测很美、实盘很惨」，多数时候不是策略失效，是两边本来就跑着两套代码。',
        deliver: '回测实盘同源框架 + 撮合模拟层，只替换数据源，不替换策略代码。',
        accept: '同一区间回放，回测与实盘两条净值曲线的偏差幅度。',
      },
      {
        name: '风控与监控台',
        pain: '出事的时候才发现没有阈值、没有熔断，人还在睡觉。',
        deliver: '回撤约束、仓位上限、熔断规则 + 一块看得懂的监控台。',
        accept: '压力回放下熔断是否按预期触发——不是看有没有配置，是看有没有真的断。',
      },
      {
        name: '数据管道与因子库',
        pain: '数据源杂、口径不一，同一个因子换个人重跑结论就变了。',
        deliver: '采集、清洗、时间对齐、落库的完整管线，口径写进代码而不是文档。',
        accept: '同一份因子隔月重跑，结果逐条一致。',
      },
      {
        name: '链上量化',
        pain: '链上的数据结构与撮合规则和传统市场不是一回事，滑点和失败交易都要重新建模。',
        deliver: '链上数据接入 + 执行适配层，复用同一套风控与监控。',
        accept: '实际成交与预期滑点的偏差，以及失败交易的重试成本。',
      },
    ],
    works: [
      {
        name: 'MoneyClaw',
        what: '7×24 无人值守的自动交易系统：信号、风控、执行、对账串成一条链路，人只在告警时介入。',
        platform: 'Web · 私有化',
        status: 'building',
        tech: ['无人值守', '风控优先', '对账告警'],
      },
      {
        name: 'ChainQuant',
        what: '链上方向的执行适配：同一套风控与监控，换掉数据源和撮合层。',
        platform: 'Web',
        status: 'building',
        tech: ['链上数据', '执行适配'],
      },
      {
        name: '信号战争',
        what: '把信号强弱与噪声的关系做成可交互的推演，用来解释「为什么大多数信号经不起交易成本」。',
        platform: 'Web',
        status: 'building',
        tech: ['信号建模', '可视化推演'],
      },
    ],
    boundary:
      '量化方向只承接工程交付：策略框架、风控规则、执行链路与监控台。不代客理财、不承诺收益、不提供投资建议。站内图表均为页面自身生成的演示数据。',
  },
};
